import { Button, Inline, SelectInput } from 'canoe';
import {
  Action,
  ActionFetch,
  connectWithHammock,
  ENDPOINTS,
  fetchResource,
  handleErrorResponse,
  useCreatePayload,
} from 'hammock';
import { debounce } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useMutation } from 'react-query';

import { ContentMetadata, UNCATEGORIZED_FOLDER_NAME } from '@candu-ai/content/types';

interface MoveSectionProps {
  contentMetadata: ContentMetadata;
  folders: { id: string; name: string }[];
  onContentMoved: VoidFunction;
  onContentMovedError?: (error: unknown, variables: ActionFetch) => void;
}
export const MoveSectionImpl = ({
  contentMetadata,
  folders,
  onContentMoved,
  onContentMovedError,
}: MoveSectionProps) => {
  const { action } = useCreatePayload(ENDPOINTS.contentMetadata);

  const { mutateAsync: moveContent, isLoading } = useMutation(
    () => {
      const payload = action(({
        query: { action: 'setFolder' },
        body: { contentIds: [contentMetadata.id], folderId: selectedFolderId },
      } as unknown) as Action);
      return fetchResource(payload).catch((e) => handleErrorResponse(e, payload));
    },
    {
      onSuccess: onContentMoved,
      onError: (e) =>
        onContentMovedError?.(
          e,
          action(({
            query: { action: 'setFolder' },
            body: { contentIds: [contentMetadata.id], folderId: selectedFolderId },
          } as unknown) as Action),
        ),
    },
  );

  const [search, setSearch] = useState<string | undefined>();
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined | null>(
    contentMetadata.folderId,
  );

  const folderItems = useMemo(() => {
    return [{ id: undefined, name: UNCATEGORIZED_FOLDER_NAME }, ...folders].map((f) => ({
      value: f.id,
      label: f.name,
    }));
  }, [folders]);

  const filteredFolderItems = useMemo(() => {
    if (!search) {
      return folderItems;
    }
    return folderItems?.filter(({ label }: { label: string }) => {
      return label.toLowerCase().includes(search?.toLowerCase());
    });
  }, [folderItems, search]);

  const updateContentsFolder = () => moveContent();

  const updateSearch = debounce((e) => {
    setSearch(e.target.value);
  }, 300);

  return (
    <>
      <SelectInput
        onSelect={(selected) => setSelectedFolderId(selected?.value)}
        items={filteredFolderItems}
        selectedItem={folderItems.find((folder) => folder.value === selectedFolderId)}
        onFilter={(e) => {
          updateSearch(e);
        }}
        onClearFilter={() => setSearch(undefined)}
        searchable
        onOpeningChange={(isOpen) => {
          if (!isOpen) {
            setSearch(undefined);
          }
        }}
      />
      <Inline justify="flex-end">
        <Button
          variant="secondary"
          disabled={selectedFolderId === contentMetadata.folderId}
          mt="xs"
          loading={isLoading}
          onClick={updateContentsFolder}
        >
          Move Content
        </Button>
      </Inline>
    </>
  );
};

const withFolders = connectWithHammock({
  endpoint: ENDPOINTS.contentFolder,
  resourceName: 'folders',
});

export const MoveSection = withFolders(MoveSectionImpl);
